import { useEffect, useId, useState } from 'react';

import { Button, Divider, Icon, IconButton } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { Checkbox, FormField, Input, Select, Switch, Textarea } from '@ds/forms';
import { moeda } from '@/comuns/formato';
import {
    custoDeConsumoCentavos, embalagensDe, fatorConversao, opcoesDeUnidade, quantidade,
    unidadeAbrev, unidadeLabel, unidadesCompativeis,
} from '@/comuns/unidades';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import type { CampoPersonalizado, CustoExtra, Procedimento, Produto, TipoDeValor } from '@/data/tipos';
import { centavos } from '@/screens/vendas/NovoOrcamentoModal';

export interface ProcedimentoModalProps {
    alvo: Procedimento | 'novo' | null;
    produtos: Produto[];
    categorias: { nome: string; total: number }[];
    profissionais: { id: string; nome: string }[];
    onClose: () => void;
    onSalvo: () => void;
}

interface LinhaDeInsumo {
    produtoId: string;
    quantidade: string;
}

const TIPOS: { value: TipoDeValor; label: string }[] = [
    { value: 'fixo', label: 'Fixo' },
    { value: 'variavel', label: 'Variável' },
    { value: 'sob-consulta', label: 'Sob consulta' },
];

const COR_PADRAO = '#D0D5DD';

export function ProcedimentoModal({ alvo, produtos, categorias, profissionais, onClose, onSalvo }: ProcedimentoModalProps) {
    const aviso = useAviso();
    const idNome = useId();
    const idValor = useId();
    const idTipo = useId();
    const idCor = useId();
    const idDuracao = useId();
    const idReconsulta = useId();
    const idCategoria = useId();
    const idDescricao = useId();

    const [nome, setNome] = useState('');
    const [valor, setValor] = useState('');
    const [tipoValor, setTipoValor] = useState<TipoDeValor>('fixo');
    const [cor, setCor] = useState(COR_PADRAO);
    const [duracao, setDuracao] = useState('60');
    const [reconsulta, setReconsulta] = useState('0');
    const [categoria, setCategoria] = useState('');
    const [ativo, setAtivo] = useState(true);
    const [equipe, setEquipe] = useState<string[]>([]);
    const [insumos, setInsumos] = useState<LinhaDeInsumo[]>([]);
    const [extras, setExtras] = useState<CustoExtra[]>([]);
    const [personalizados, setPersonalizados] = useState<CampoPersonalizado[]>([]);
    const [descricao, setDescricao] = useState('');
    const formSujo = useAlteracaoPendente(
        { nome, valor, tipoValor, cor, duracao, reconsulta, categoria, ativo, equipe, insumos, extras, personalizados, descricao },
        alvo,
    );
    const [avancadas, setAvancadas] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    const novo = alvo === 'novo';

    useEffect(() => {
        if (!alvo) return;
        setErro(null);
        setAvancadas(false);
        if (alvo === 'novo') {
            setNome('');
            setValor('');
            setTipoValor('fixo');
            setCor(COR_PADRAO);
            setDuracao('60');
            setReconsulta('0');
            setCategoria(categorias[0]?.nome ?? '');
            setAtivo(true);
            setEquipe([]);
            setInsumos([]);
            setExtras([]);
            setPersonalizados([]);
            setDescricao('');
            return;
        }
        setNome(alvo.nome);
        setValor((alvo.precoCentavos / 100).toFixed(2).replace('.', ','));
        setTipoValor(alvo.tipoValor);
        setCor(alvo.cor ?? COR_PADRAO);
        setDuracao(String(alvo.duracaoMin));
        setReconsulta(String(alvo.reconsultaDias));
        setCategoria(alvo.categoria ?? '');
        setAtivo(alvo.ativo);
        setEquipe(alvo.profissionais.map((p) => p.id));
        setInsumos(alvo.insumos.map((i) => ({ produtoId: i.produtoId, quantidade: String(i.quantidade) })));
        setExtras(alvo.custosExtras.map((c) => ({ ...c })));
        setPersonalizados(alvo.camposPersonalizados.map((c) => ({ ...c })));
        setDescricao(alvo.descricao ?? '');
    }, [alvo, categorias]);

    if (!alvo) return null;

    const produtoDe = (id: string) => produtos.find((p) => p.id === id);
    const custoDaLinha = (linha: LinhaDeInsumo) => {
        const produto = produtoDe(linha.produtoId);
        return produto ? custoDeConsumoCentavos(produto) * (Number(linha.quantidade.replace(',', '.')) || 0) : 0;
    };
    const custoDeInsumos = insumos.reduce((s, linha) => s + custoDaLinha(linha), 0);
    const custoExtra = extras.reduce((s, c) => s + c.quantidade * c.valorCentavos, 0);
    const custoTotal = custoDeInsumos + custoExtra;
    const precoCentavos = centavos(valor);

    /* Trocar o produto troca a escala. Quando a dimensão é a mesma, converte
       (2.000 µl viram 2 ml); quando não é, manter o número enganaria — 2.000 µl
       não são 2.000 UI —, então a quantidade recomeça em 1. */
    const trocarProduto = (indice: number, produtoId: string) => {
        setInsumos((linhas) => linhas.map((linha, i) => {
            if (i !== indice) return linha;
            const antigo = produtoDe(linha.produtoId);
            const novoProduto = produtoDe(produtoId);
            const de = antigo?.unidadeConsumo;
            const para = novoProduto?.unidadeConsumo;
            const compativel = de && para && (de === para || unidadesCompativeis(de).includes(para));
            const atual = Number(linha.quantidade.replace(',', '.')) || 0;
            return {
                produtoId,
                quantidade: compativel ? String(Math.round(atual * fatorConversao(de, para) * 100) / 100) : '1',
            };
        }));
    };

    const salvar = async () => {
        setSalvando(true);
        try {
            await dados().salvarProcedimento({
                id: novo ? undefined : alvo.id,
                nome,
                categoria: categoria || undefined,
                duracaoMin: Number(duracao) || 0,
                precoCentavos,
                tipoValor,
                reconsultaDias: Number(reconsulta) || 0,
                cor,
                descricao: descricao || undefined,
                ativo,
                profissionaisIds: equipe,
                insumos: insumos
                    .filter((linha) => linha.produtoId)
                    .map((linha) => ({ produtoId: linha.produtoId, quantidade: Number(linha.quantidade.replace(',', '.')) || 0 })),
                custosExtras: extras,
                camposPersonalizados: personalizados,
            });
            aviso({
                title: novo ? 'Procedimento cadastrado' : 'Procedimento atualizado',
                description: `${nome} · disponível na agenda e nos orçamentos`,
            });
            onSalvo();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={720}
            title={novo ? 'Novo procedimento' : 'Editar procedimento'}
            description="Preço, duração e o que sai do estoque a cada execução."
            dirty={formSujo}
            onSave={() => void salvar()}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void salvar()} loading={salvando} disabled={!nome.trim()}>
                        {novo ? 'Cadastrar' : 'Salvar'}
                    </Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                <FormField label="Nome" required htmlFor={idNome}>
                    <Input id={idNome} value={nome} onChange={(v) => { setNome(v); setErro(null); }} placeholder="Ex.: Limpeza de pele profunda" />
                </FormField>

                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr) minmax(0,1fr)', gap: 14 }}>
                    <FormField label="Valor de venda" required htmlFor={idValor} hint="Zero é válido — o retorno é gratuito">
                        <span style={{ display: 'flex', gap: 8 }}>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                <Input id={idValor} value={valor} onChange={setValor} placeholder="0,00" />
                            </span>
                            <span style={{ width: 128, flex: '0 0 auto' }}>
                                <Select id={idTipo} value={tipoValor} onChange={(v) => setTipoValor(v as TipoDeValor)} options={TIPOS} />
                            </span>
                        </span>
                    </FormField>
                    <FormField label="Custo total" hint="Insumos mais custos extras — calculado">
                        <Input value={moeda(custoTotal)} onChange={() => {}} disabled />
                    </FormField>
                    {/* Seletor de cor, não paleta fixa: cada clínica organiza a
                        agenda pelo próprio critério, e seis cores prontas viram
                        empate visual no sétimo procedimento. */}
                    <FormField label="Cor na agenda" htmlFor={idCor}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 10, height: 36 }}>
                            <input
                                id={idCor} type="color" value={cor} onChange={(ev) => setCor(ev.target.value)}
                                aria-label="Cor do procedimento na agenda"
                                style={{ width: 36, height: 30, padding: 0, border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', background: 'transparent', cursor: 'pointer' }}
                            />
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{cor}</span>
                        </span>
                    </FormField>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) minmax(0,1.6fr)', gap: 14 }}>
                    <FormField label="Duração" required htmlFor={idDuracao} hint="Minutos — define o bloco na agenda">
                        <Input id={idDuracao} type="number" value={duracao} onChange={setDuracao} />
                    </FormField>
                    <FormField label="Tempo de reconsulta" htmlFor={idReconsulta} hint="Dias até o retorno sugerido">
                        <Input id={idReconsulta} type="number" value={reconsulta} onChange={setReconsulta} />
                    </FormField>
                    <FormField label="Categoria" htmlFor={idCategoria}>
                        <Input id={idCategoria} value={categoria} onChange={setCategoria} placeholder="Injetável, Estética, Consulta" />
                    </FormField>
                </div>

                {/* Vínculo, e não texto: "quem pode executar isto?" é pergunta
                    que a agenda faz, e nome digitado não responde. */}
                <FormField label="Profissionais que executam" hint="Aparecem como opção ao agendar">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        {profissionais.map((p) => (
                            <Checkbox
                                key={p.id} checked={equipe.includes(p.id)} label={p.nome}
                                onChange={(marcado) => setEquipe((atual) => (marcado ? [...atual, p.id] : atual.filter((id) => id !== p.id)))}
                            />
                        ))}
                    </div>
                </FormField>

                <Switch
                    checked={ativo} label="Ativo" onChange={setAtivo}
                    description="Inativo não aparece na agenda nem nos orçamentos; o histórico já realizado é preservado"
                />

                <Divider label="Materiais de atendimento" spacing={2} />

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {insumos.map((linha, indice) => {
                        const produto = produtoDe(linha.produtoId);
                        const qtd = Number(linha.quantidade.replace(',', '.')) || 0;
                        return (
                            <div key={indice} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    {/* A opção declara o saldo: escolher insumo sem ver o
                                        estoque faz nascer procedimento impossível de executar. */}
                                    <Select
                                        value={linha.produtoId} onChange={(v) => trocarProduto(indice, v)}
                                        aria-label={`Insumo ${indice + 1}`}
                                        options={produtos.map((p) => ({
                                            value: p.id,
                                            label: `${p.nome} — ${quantidade(p.disponivel)} ${unidadeLabel(p.disponivel, p.unidade)} · debita em ${unidadeAbrev(p.unidadeConsumo)}`,
                                        }))}
                                    />
                                </span>
                                <span style={{ width: 96, flex: '0 0 auto' }}>
                                    <Input
                                        value={linha.quantidade} aria-label={`Quantidade do insumo ${indice + 1}`}
                                        onChange={(v) => setInsumos((linhas) => linhas.map((x, i) => (i === indice ? { ...x, quantidade: v } : x)))}
                                    />
                                </span>
                                {/* Unidade HERDADA do cadastro do produto: campo livre aqui
                                    permitiria lançar 2.000 "seringa", e o custo sairia
                                    multiplicado pela embalagem. */}
                                <span
                                    title={produto ? 'Definido no cadastro do produto' : ''}
                                    style={{ width: 120, flex: '0 0 auto', display: 'flex', alignItems: 'center', height: 36, padding: '0 11px', background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-input)', fontSize: 13, color: 'var(--text-secondary)' }}
                                >
                                    {produto ? unidadeAbrev(produto.unidadeConsumo) : '—'}
                                </span>
                                <span style={{ width: 116, flex: '0 0 auto', textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                                    <span style={{ display: 'block', fontWeight: 600 }}>{produto ? moeda(custoDaLinha(linha)) : '—'}</span>
                                    {produto && (
                                        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                                            {quantidade(embalagensDe(produto, qtd))} {unidadeLabel(embalagensDe(produto, qtd), produto.unidade)}
                                        </span>
                                    )}
                                </span>
                                <IconButton
                                    size="sm" icon={<Icon name="trash-2" size={14} />} label={`Remover insumo ${indice + 1}`}
                                    onClick={() => setInsumos((linhas) => linhas.filter((_, i) => i !== indice))}
                                />
                            </div>
                        );
                    })}
                    <Button
                        variant="ghost" size="sm" iconLeft={<Icon name="plus" size={14} />}
                        style={{ alignSelf: 'flex-start' }}
                        disabled={produtos.length === 0}
                        onClick={() => setInsumos((linhas) => [...linhas, { produtoId: produtos[0]?.id ?? '', quantidade: '1' }])}
                    >
                        Adicionar material
                    </Button>
                </div>

                <Divider label="Custos extras" spacing={2} />

                {/* O que entra no preço e não sai do estoque: descarte, luva,
                    serviço de terceiro. Sem eles a margem sai otimista. */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {extras.map((c, indice) => (
                        <div key={indice} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                <Input
                                    value={c.nome} placeholder="Nome do custo extra" aria-label={`Custo extra ${indice + 1}`}
                                    onChange={(v) => setExtras((lista) => lista.map((x, i) => (i === indice ? { ...x, nome: v } : x)))}
                                />
                            </span>
                            <span style={{ width: 78, flex: '0 0 auto' }}>
                                <Input
                                    type="number" value={String(c.quantidade)} aria-label={`Quantidade do custo extra ${indice + 1}`}
                                    onChange={(v) => setExtras((lista) => lista.map((x, i) => (i === indice ? { ...x, quantidade: Number(v) || 0 } : x)))}
                                />
                            </span>
                            <span style={{ width: 132, flex: '0 0 auto' }}>
                                <Select
                                    value={c.unidade} aria-label={`Unidade do custo extra ${indice + 1}`}
                                    onChange={(v) => setExtras((lista) => lista.map((x, i) => (i === indice ? { ...x, unidade: v } : x)))}
                                    options={opcoesDeUnidade()}
                                />
                            </span>
                            <span style={{ width: 116, flex: '0 0 auto' }}>
                                <Input
                                    value={(c.valorCentavos / 100).toFixed(2).replace('.', ',')} placeholder="0,00"
                                    aria-label={`Valor do custo extra ${indice + 1}`}
                                    onChange={(v) => setExtras((lista) => lista.map((x, i) => (i === indice ? { ...x, valorCentavos: centavos(v) } : x)))}
                                />
                            </span>
                            <IconButton
                                size="sm" icon={<Icon name="trash-2" size={14} />} label={`Remover custo extra ${indice + 1}`}
                                onClick={() => setExtras((lista) => lista.filter((_, i) => i !== indice))}
                            />
                        </div>
                    ))}
                    <Button
                        variant="ghost" size="sm" iconLeft={<Icon name="plus" size={14} />} style={{ alignSelf: 'flex-start' }}
                        onClick={() => setExtras((lista) => [...lista, { nome: '', quantidade: 1, unidade: 'und', valorCentavos: 0 }])}
                    >
                        Adicionar custo extra
                    </Button>
                </div>

                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
                    <span style={{ fontFamily: 'var(--font-ui)', fontSize: 14, fontWeight: 600 }}>Custo total</span>
                    <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-ui)', fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {moeda(custoTotal)}
                    </span>
                    <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                        {precoCentavos > 0 ? `margem de ${Math.round(((precoCentavos - custoTotal) / precoCentavos) * 100)}%` : 'sem preço, sem margem'}
                    </span>
                </div>

                <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14 }}>
                    <button
                        type="button" onClick={() => setAvancadas((v) => !v)} aria-expanded={avancadas}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', border: 0, background: 'transparent', cursor: 'pointer', padding: '2px 0', textAlign: 'left' }}
                    >
                        <span style={{ flex: 1, fontFamily: 'var(--font-ui)', fontSize: 14, fontWeight: 600 }}>Informações adicionais</span>
                        <Icon name="chevron-down" size={16} style={{ transform: avancadas ? 'none' : 'rotate(-90deg)' }} />
                    </button>

                    {avancadas && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
                            <FormField label="Descrição para o paciente" htmlFor={idDescricao} hint="Usada em orçamentos e documentos">
                                <Textarea id={idDescricao} rows={3} value={descricao} onChange={setDescricao} />
                            </FormField>

                            {/* Campo personalizado: o que o produto não previu tem lugar
                                próprio, senão vira texto solto na descrição e ninguém
                                consegue relatar depois. */}
                            {personalizados.map((c, indice) => (
                                <div key={indice} style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                                    <span style={{ width: 220, flex: '0 0 auto' }}>
                                        <Input
                                            value={c.rotulo} placeholder="Nome do campo" aria-label={`Campo personalizado ${indice + 1}`}
                                            onChange={(v) => setPersonalizados((lista) => lista.map((x, i) => (i === indice ? { ...x, rotulo: v } : x)))}
                                        />
                                    </span>
                                    <span style={{ flex: 1, minWidth: 0 }}>
                                        <Input
                                            value={c.valor} placeholder="Conteúdo" aria-label={`Valor do campo personalizado ${indice + 1}`}
                                            onChange={(v) => setPersonalizados((lista) => lista.map((x, i) => (i === indice ? { ...x, valor: v } : x)))}
                                        />
                                    </span>
                                    <IconButton
                                        size="sm" icon={<Icon name="trash-2" size={14} />} label={`Remover campo personalizado ${indice + 1}`}
                                        onClick={() => setPersonalizados((lista) => lista.filter((_, i) => i !== indice))}
                                    />
                                </div>
                            ))}
                            <Button
                                variant="ghost" size="sm" iconLeft={<Icon name="plus" size={14} />} style={{ alignSelf: 'flex-start' }}
                                onClick={() => setPersonalizados((lista) => [...lista, { rotulo: '', valor: '' }])}
                            >
                                Adicionar campo personalizado
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}
